/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { Fragment, useMemo, useEffect, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

// Material Dashboard 2 React example components
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import {
  paginationContainedButtonSx,
  secondaryOutlinedButtonSx,
} from "components/common/buttonStyles";

const grayIconNavigationSx = {
  color: "#fff",
  backgroundColor: "#6b7280",
  boxShadow: "none",
  "&:hover": { backgroundColor: "#4b5563", boxShadow: "none" },
  "&.Mui-disabled": {
    color: "#fff",
    backgroundColor: "#6b7280",
    boxShadow: "none",
    opacity: 1,
    WebkitTextFillColor: "#fff",
  },
};

/* File: src/examples/Tables/DataTable/index.jsx  (del template)
 * Tabla genérica del template (MUI) que recibe { columns, rows }.
 * La usamos como “engine” de render; no contiene reglas de negocio.
 * Evitar modificar salvo ajustes visuales globales.
 */

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  disablePagination,
  resultsPagination,
  resultsPaginationOptions,
  hideFooter,
  fixedColumnWidths,
  resetPageKey,
  emptyMessage,
  renderExpandedRow,
}) {
  const {
    showCount = true,
    hidePageOnEmpty = false,
    alignRight = false,
    compact = false,
    shortPageLabel = false,
    iconNavigation = false,
    grayIconNavigation = false,
    pageSize: fixedPageSize,
    showPageSizeSelector = true,
    hideNavigationOnSinglePage = false,
  } = resultsPaginationOptions;
  const defaultValue = fixedPageSize ?? (entriesPerPage.defaultValue || 10);
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ["5", "10", "15", "20", "25"];
  const columns = useMemo(() => table.columns, [table.columns]);
  const data = useMemo(() => table.rows, [table.rows]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: defaultValue },
      autoResetPage: false,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

  // Set the default value for the entries per page when component mounts
  useEffect(() => setPageSize(defaultValue || 10), [defaultValue]);

  useEffect(() => {
    if (resetPageKey !== undefined) gotoPage(0);
  }, [resetPageKey, gotoPage]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value) => setPageSize(value);

  // Render the paginations
  const renderPagination = pageOptions.map((option) => (
    <MDPagination
      item
      key={option}
      onClick={() => gotoPage(Number(option))}
      active={pageIndex === option}
    >
      {option + 1}
    </MDPagination>
  ));

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }) =>
    value > pageOptions.length || value < 0 ? gotoPage(0) : gotoPage(Number(value));

  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option) => option + 1);

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: value }) => gotoPage(Number(value.value - 1));

  // Search input value state
  const [search, setSearch] = useState(globalFilter);

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  // Setting the entries starting point
  const entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;

  // Setting the entries ending point
  let entriesEnd;

  if (pageIndex === 0) {
    entriesEnd = pageSize;
  } else if (pageIndex === pageOptions.length - 1) {
    entriesEnd = rows.length;
  } else {
    entriesEnd = pageSize * (pageIndex + 1);
  }

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.max(1, Math.min(pageIndex + 1, totalPages));
  const visibleRows = resultsPagination
    ? rows.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : disablePagination
      ? rows
      : page;

  useEffect(() => {
    if (resultsPagination && pageIndex >= totalPages) gotoPage(totalPages - 1);
  }, [resultsPagination, pageIndex, totalPages, gotoPage]);

  const changeResultsPageSize = (value) => {
    setPageSize(value);
    gotoPage(0);
  };

  return (
    <TableContainer sx={{ boxShadow: "none" }}>
      {entriesPerPage || canSearch ? (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
          {entriesPerPage && (
            <MDBox display="flex" alignItems="center">
              <Autocomplete
                disableClearable
                value={pageSize.toString()}
                options={entries}
                onChange={(event, newValue) => {
                  setEntriesPerPage(parseInt(newValue, 10));
                }}
                size="small"
                sx={{ width: "5rem" }}
                renderInput={(params) => <MDInput {...params} />}
              />
              <MDTypography variant="caption" color="secondary">
                &nbsp;&nbsp;filas por página
              </MDTypography>
            </MDBox>
          )}
          {canSearch && (
            <MDBox width="12rem" ml="auto">
              <MDInput
                placeholder="Buscar..."
                value={search}
                size="small"
                fullWidth
                onChange={({ currentTarget }) => {
                  setSearch(currentTarget.value);
                  onSearchChange(currentTarget.value);
                }}
              />
            </MDBox>
          )}
        </MDBox>
      ) : null}
      <Table
        {...getTableProps()}
        sx={fixedColumnWidths ? { tableLayout: "fixed", minWidth: 880 } : undefined}
      >
        {fixedColumnWidths && (
          <colgroup>
            {fixedColumnWidths.map((width, index) => (
              <col key={index} style={{ width }} />
            ))}
          </colgroup>
        )}
        <MDBox component="thead">
          {headerGroups.map((headerGroup, key) => (
            <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, idx) => (
                <DataTableHeadCell
                  key={idx}
                  {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                  width={column.width ? column.width : "auto"}
                  align={column.align ? column.align : "left"}
                  sorted={setSortedValue(column)}
                >
                  {column.render("Header")}
                </DataTableHeadCell>
              ))}
            </TableRow>
          ))}
        </MDBox>
        <TableBody {...getTableBodyProps()}>
          {emptyMessage && visibleRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length} align="center">
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
          {visibleRows.map((row, key) => {
            prepareRow(row);
            const expandedContent = renderExpandedRow?.(row.original);
            const isCounterVerificationDetailRow =
              row.original?.__isCounterVerificationDetailRow === true ||
              row.original?.__isAdditionalSourceRow === true;
            const useFullWidthCells =
              row.original?.__useFullWidthCells === true || isCounterVerificationDetailRow;
            return (
              <Fragment key={row.id}>
                <TableRow {...row.getRowProps()}>
                  {row.cells.map((cell, idx) => (
                    <DataTableBodyCell
                      key={idx}
                      noBorder={noEndBorder && rows.length - 1 === key}
                      align={cell.column.align ? cell.column.align : "left"}
                      verticalAlign={useFullWidthCells ? "top" : "middle"}
                      fullWidthContent={useFullWidthCells}
                      {...cell.getCellProps()}
                    >
                      {cell.render("Cell")}
                    </DataTableBodyCell>
                  ))}
                </TableRow>
                {expandedContent && (
                  <TableRow>
                    <TableCell colSpan={columns.length} sx={{ p: 0 }}>
                      {expandedContent}
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>

      {resultsPagination ? (
        <MDBox
          px={compact ? 3 : 1}
          pt={compact ? 0.5 : 2}
          pb={compact ? 0.5 : 0}
          display="flex"
          justifyContent={alignRight ? "flex-end" : "space-between"}
          alignItems={{ xs: alignRight ? "flex-end" : "flex-start", md: "center" }}
          flexDirection={{ xs: "column", md: "row" }}
          gap={compact ? 1 : 2}
          sx={{ minWidth: 0 }}
        >
          {showCount && (
            <MDTypography variant="button" color="secondary">
              {`Mostrando ${visibleRows.length} de ${rows.length} resultados`}
            </MDTypography>
          )}
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent={alignRight ? "flex-end" : undefined}
            gap={1}
            flexWrap="wrap"
            sx={{ minWidth: 0 }}
          >
            {(!hidePageOnEmpty || rows.length > 0) && (
              <MDTypography variant="button" color="secondary">
                {`${shortPageLabel ? "Pág." : "Página"} ${currentPage} de ${totalPages}`}
              </MDTypography>
            )}
            {showPageSizeSelector && (
              <TextField
                select
                size="small"
                label="Filas"
                value={String(pageSize)}
                onChange={(event) => changeResultsPageSize(Number(event.target.value))}
                sx={{ minWidth: 100, "& .MuiInputBase-root": { height: 40 } }}
              >
                {[10, 25].map((size) => (
                  <MenuItem key={size} value={String(size)}>
                    {size}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {(!hideNavigationOnSinglePage || totalPages > 1) &&
              (iconNavigation ? (
                <>
                  <Tooltip title="Página anterior" disableInteractive>
                    <span style={{ display: "inline-flex" }}>
                      <Button
                        variant={grayIconNavigation ? "contained" : "outlined"}
                        color={grayIconNavigation ? "secondary" : "info"}
                        aria-label="Página anterior"
                        disabled={currentPage === 1}
                        sx={{
                          ...(grayIconNavigation
                            ? grayIconNavigationSx
                            : secondaryOutlinedButtonSx),
                          minWidth: 40,
                          width: 40,
                          height: 40,
                          p: 0,
                        }}
                        onClick={() => gotoPage(currentPage - 2)}
                      >
                        <ChevronLeftIcon />
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Página siguiente" disableInteractive>
                    <span style={{ display: "inline-flex" }}>
                      <Button
                        variant="contained"
                        color={grayIconNavigation ? "secondary" : "info"}
                        aria-label="Página siguiente"
                        disabled={currentPage === totalPages}
                        sx={{
                          ...(grayIconNavigation
                            ? grayIconNavigationSx
                            : paginationContainedButtonSx),
                          minWidth: 40,
                          width: 40,
                          height: 40,
                          p: 0,
                        }}
                        onClick={() => gotoPage(currentPage)}
                      >
                        <ChevronRightIcon />
                      </Button>
                    </span>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    color="info"
                    disabled={currentPage === 1}
                    sx={secondaryOutlinedButtonSx}
                    onClick={() => gotoPage(currentPage - 2)}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="contained"
                    color="info"
                    disabled={currentPage === totalPages}
                    sx={paginationContainedButtonSx}
                    onClick={() => gotoPage(currentPage)}
                  >
                    Siguiente
                  </Button>
                </>
              ))}
          </MDBox>
        </MDBox>
      ) : hideFooter ? null : (
        <MDBox
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          p={!showTotalEntries && (disablePagination || pageOptions.length === 1) ? 0 : 3}
        >
          {showTotalEntries && (
            <MDBox mb={{ xs: 3, sm: 0 }}>
              <MDTypography variant="button" color="secondary" fontWeight="regular">
                Mostrando {entriesStart} a {entriesEnd} de {rows.length} filas
              </MDTypography>
            </MDBox>
          )}
          {!disablePagination && pageOptions.length > 1 && (
            <MDPagination
              variant={pagination.variant ? pagination.variant : "gradient"}
              color={pagination.color ? pagination.color : "info"}
            >
              {canPreviousPage && (
                <MDPagination item onClick={() => previousPage()}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                </MDPagination>
              )}
              {renderPagination.length > 6 ? (
                <MDBox width="5rem" mx={1}>
                  <MDInput
                    inputProps={{ type: "number", min: 1, max: customizedPageOptions.length }}
                    value={customizedPageOptions[pageIndex]}
                    onChange={(handleInputPagination, handleInputPaginationValue)}
                  />
                </MDBox>
              ) : (
                renderPagination
              )}
              {canNextPage && (
                <MDPagination item onClick={() => nextPage()}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                </MDPagination>
              )}
            </MDPagination>
          )}
        </MDBox>
      )}
    </TableContainer>
  );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
  disablePagination: false,
  resultsPagination: false,
  resultsPaginationOptions: {},
  hideFooter: false,
  fixedColumnWidths: null,
  resetPageKey: undefined,
  emptyMessage: null,
  renderExpandedRow: undefined,
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  disablePagination: PropTypes.bool,
  resultsPagination: PropTypes.bool,
  resultsPaginationOptions: PropTypes.shape({
    showCount: PropTypes.bool,
    hidePageOnEmpty: PropTypes.bool,
    alignRight: PropTypes.bool,
    compact: PropTypes.bool,
    shortPageLabel: PropTypes.bool,
    iconNavigation: PropTypes.bool,
    grayIconNavigation: PropTypes.bool,
    pageSize: PropTypes.number,
    showPageSizeSelector: PropTypes.bool,
    hideNavigationOnSinglePage: PropTypes.bool,
  }),
  hideFooter: PropTypes.bool,
  fixedColumnWidths: PropTypes.arrayOf(PropTypes.string),
  resetPageKey: PropTypes.number,
  emptyMessage: PropTypes.string,
  renderExpandedRow: PropTypes.func,
};

export default DataTable;
